import { DeleteFilled, PlusOutlined } from '@ant-design/icons';
import { Button, Card, Col, Form, Input, Row, Select, Switch } from 'antd';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { isUri } from 'valid-url';
import { LocatorType, LocatorTypeOptions } from '../utils/locatorTypes';
import { useConfigurationCtx } from './ConfigurationContext';

const LocatorGrid = Card.Grid;

const StyledCard = styled(Card)`
  &&& {
    margin-top: 15px;
  }
  &&& .ant-card-body {
    padding: 0;
    margin: -1px 0 0 -1px;
  }
  &&& .ant-card-grid {
    width: 100%;
    padding: 12px;
  }
`;
export interface ILocatorConfigurationProps {
  hideUseInMap?: boolean;
}

export const LocatorConfiguration = ({ hideUseInMap }: ILocatorConfigurationProps) => {
  const { t } = useTranslation('geosecma-settings');
  const { getLocatorSuggestions, getProxyUrl } = useConfigurationCtx();
  const [selectOpen, setSelectOpen] = React.useState(false);

  const validateUrl = async (_: any, value: string) => {
    const validUrl = isUri(value);
    const validLocatorUrl = validUrl && value.endsWith('/GeocodeServer');

    if (!validLocatorUrl && value !== '' && value !== undefined) {
      return Promise.reject(new Error(t('urlValidationError')));
    } else if (value !== '' && value !== undefined) {
      const errorMessage = await validateLocator(value);
      if (errorMessage !== undefined) {
        return Promise.reject(errorMessage);
      }
    }

    return Promise.resolve();
  };

  const validateLocator = async (url: string) => {
    const proxyUrl = getProxyUrl();
    const request = proxyUrl !== '' ? `${proxyUrl}?${url}?f=json` : `${url}?f=json`;

    try {
      const response = await getLocatorSuggestions(request);
      if (response.error) {
        if (response.error.message.includes('not found')) {
          return t('locatorNotFound');
        }

        return response.error.message;
      } else if (!response.capabilities.includes('Suggest')) {
        return t('locatorNoSuggest');
      }
    } catch {
      return t('locatorNotFound');
    }
  };

  const addForm = (
    <Form.List name="locators">
      {(fields, { add, remove }) => (
        <>
          {fields.map((field) => (
            <LocatorGrid hoverable={false} key={field.key}>
              <Row gutter={16} align="middle">
                <Col span={24}>
                  <Form.Item
                    {...field}
                    label={t('locatorUrl')}
                    name={[field.name, 'url']}
                    fieldKey={[field.key, 'url']}
                    hasFeedback
                    rules={[{ required: true, message: t('urlRequired') }, { validator: validateUrl }]}
                  >
                    <Input
                      data-testid={`locatorUrlInput${field.key}`}
                      placeholder={'https://host.domain.com/arcgis/rest/services/locator/GeocodeServer'}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    {...field}
                    label={t('locatorType')}
                    name={[field.name, 'locatorType']}
                    fieldKey={[field.key, 'locatorType']}
                  >
                    <Select
                      aria-expanded={selectOpen}
                      aria-label={t('locatorType')}
                      onDropdownVisibleChange={setSelectOpen}
                      data-testid={`locatorTypeInput${field.key}`}
                      dropdownMatchSelectWidth={false}
                    >
                      {LocatorTypeOptions(t).map((option) => (
                        <Select.Option key={option.code} value={option.code}>
                          {`${option.description}`}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    {...field}
                    label={t('useInMap')}
                    name={[field.name, 'useInMap']}
                    fieldKey={[field.key, 'useInMap']}
                    valuePropName="checked"
                    hidden={hideUseInMap}
                  >
                    <Switch
                      aria-label={t('useInMap')}
                      defaultChecked={true}
                      data-testid={`locatorUseInMap${field.key}`}
                      size="small"
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Row justify="end">
                    <Button
                      data-testid={`removeLocatorButton${field.key}`}
                      icon={<DeleteFilled />}
                      onClick={() => remove(field.name)}
                    >
                      {t('remove')}
                    </Button>
                  </Row>
                </Col>
              </Row>
            </LocatorGrid>
          ))}
          <LocatorGrid hoverable={false}>
            <Button
              data-testid="addLocatorButton"
              onClick={() =>
                add({
                  url: '',
                  locatorType: LocatorType.Other,
                  useInMap: true
                })
              }
              block
              icon={<PlusOutlined />}
            >
              {t('addLocator')}
            </Button>
          </LocatorGrid>
        </>
      )}
    </Form.List>
  );

  return (
    <StyledCard title={t('locators')} size="small" type="inner">
      <LocatorGrid hoverable={false}>{t('locatorExplanation')}</LocatorGrid>
      {addForm}
    </StyledCard>
  );
};
